import type { DocumentModel } from '@ctw/shared/api/fhir/models/document';
import { ExpandableText } from '@ctw/shared/components/typography/expandable-text';
import {
  RenderSyncedWithRecordIcon,
  ResourceTitleColumn,
} from '@ctw/shared/content/resource/resource-title-column';
import { useCTW } from '@ctw/shared/context/ctw-context';

export const useDocumentColumns = () => {
  const { requestContext } = useCTW();
  const userBuilderId = requestContext.builderId;

  const columns = [
    {
      widthPercent: 20,
      minWidth: 100,
      title: 'Title',
      render: (document: DocumentModel) => {
        const contentType = document.contentTypeDisplay;
        return (
          <ResourceTitleColumn
            title={`${document.title}${contentType ? ` (${contentType})` : ''}`}
            subTitle={document.custodian}
            renderIcon={RenderSyncedWithRecordIcon(document.ownedByBuilder(userBuilderId))}
          />
        );
      },
    },
    {
      minWidth: 128,
      title: 'Encounter Date',
      render: (document: DocumentModel, isStacked?: boolean) => {
        if (isStacked) {
          return document.encounterDate ? `Encounter: ${document.encounterDate}` : undefined;
        }
        return document.encounterDate;
      },
    },
    {
      minWidth: 128,
      title: 'Date Retrieved',
      render: (document: DocumentModel, isStacked?: boolean) => {
        if (isStacked) {
          return document.dateCreated ? `Retrieved: ${document.dateCreated}` : undefined;
        }
        return document.dateCreated;
      },
    },
    {
      minWidth: 35,
      title: 'Type',
      render: (document: DocumentModel) => {
        return document.typeDisplay ?? document.loincTypeCode;
      },
    },
    {
      minWidth: 128,
      widthPercent: 35,
      title: 'Sections',
      render: (document: DocumentModel, isStacked?: boolean) => {
        const previewCount = 6;
        const sectionCount = document.sectionCount();
        const previewDisplay = document.sectionsDisplayPreview(previewCount);
        const fullDisplay = document.sectionsDisplayFull();

        if (
          isStacked ||
          !document.isCDA ||
          sectionCount === 0 ||
          previewDisplay === undefined ||
          fullDisplay === undefined
        ) {
          return undefined;
        }

        if (sectionCount <= previewCount) {
          return fullDisplay;
        }

        return (
          <ExpandableText
            collapsedText={previewDisplay}
            expandedText={fullDisplay}
            expandButtonLabel={`+ ${sectionCount - previewCount} more`}
          />
        );
      },
    },
  ];

  return columns;
};
